import { useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  buildAutoDashboard,
  buildDataSummary,
  groupBy,
  type Aggregation,
  type ColumnMeta,
  type Row,
} from '@/lib/biData';
import ChartCard from './ChartCard';

// ============================================================
// Zifek BI — tableau de bord automatique + constructeur manuel
// + insights IA. Les graphiques sont réellement basés sur les données.
// ============================================================

interface Insight { title: string; detail: string; }

const AGGS: Aggregation[] = ['SUM', 'AVG', 'COUNT', 'MIN', 'MAX', 'DISTINCT'];

export default function AutoDashboard({
  columns,
  rows,
  datasetName,
  onCrossFilter,
}: {
  columns: ColumnMeta[];
  rows: Row[];
  datasetName: string;
  onCrossFilter?: (column: string, value: string) => void;
}) {
  const auto = useMemo(() => buildAutoDashboard(columns, rows), [columns, rows]);

  const [dimension, setDimension] = useState(auto.dimensionColumns[0] || '');
  const [measure, setMeasure] = useState(auto.numericColumns[0] || '');
  const [agg, setAgg] = useState<Aggregation>('SUM');
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie' | 'column' | 'area'>('bar');
  const [manualPoints, setManualPoints] = useState<{ label: string; value: number }[] | null>(null);

  const [insights, setInsights] = useState<Insight[]>([]);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [insightError, setInsightError] = useState('');

  const generateManual = () => {
    if (!dimension) return;
    const measureCol = agg === 'COUNT' ? dimension : measure;
    if (!measureCol) return;
    const pts = groupBy(rows, dimension, measureCol, agg).slice(0, 20);
    setManualPoints(pts);
  };

  const generateInsights = async () => {
    setLoadingInsights(true);
    setInsightError('');
    try {
      const summary = buildDataSummary(datasetName, columns, rows);
      const { data, error } = await supabase.functions.invoke('bi-ai', {
        body: { mode: 'insights', summary },
      });
      if (error) throw error;
      if (data?.error && !data?.insights) throw new Error(data.error);
      setInsights(data?.insights || []);
    } catch (err) {
      setInsightError(err instanceof Error ? err.message : 'Erreur de génération');
    } finally {
      setLoadingInsights(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {auto.kpis.map((k) => (
          <div key={k.label} className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <p className="text-[11px] text-foreground-500 truncate">{k.label}</p>
            <p className="text-lg font-bold font-heading text-foreground-950">
              {k.value.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
            </p>
          </div>
        ))}
      </div>

      {/* Insights IA */}
      <div className="bg-accent-50 border border-accent-200/50 rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="text-sm font-semibold text-accent-800 flex items-center gap-2">
            <i className="ri-lightbulb-line"></i>Analyse IA
          </h3>
          <button
            onClick={generateInsights}
            disabled={loadingInsights}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-500 text-background-50 text-xs font-medium whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {loadingInsights ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-magic-line"></i>}
            {insights.length > 0 ? 'Régénérer' : 'Générer l’analyse'}
          </button>
        </div>
        {insightError && <p className="text-xs text-red-600 mb-2">{insightError}</p>}
        {insights.length === 0 && !loadingInsights ? (
          <p className="text-xs text-accent-700">Cliquez sur « Générer l&apos;analyse » pour obtenir les tendances, anomalies et points clés de vos données.</p>
        ) : (
          <ul className="space-y-2">
            {insights.map((ins, i) => (
              <li key={i} className="bg-background-50 rounded-lg p-3">
                <p className="text-xs font-semibold text-foreground-900">{ins.title}</p>
                <p className="text-xs text-foreground-600 mt-0.5">{ins.detail}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Graphiques automatiques */}
      {auto.charts.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {auto.charts.map((c) => (
            <ChartCard
              key={c.title}
              title={c.title}
              chartType={c.chartType}
              points={c.points}
              onSelect={onCrossFilter ? (label) => onCrossFilter(c.dimension, label) : undefined}
            />
          ))}
        </div>
      )}

      {/* Constructeur manuel */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
          <i className="ri-add-circle-line text-primary-500"></i>Créer une visualisation
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          <div>
            <label className="block text-[11px] font-medium text-foreground-600 mb-1">Dimension</label>
            <select value={dimension} onChange={(e) => setDimension(e.target.value)} className="w-full px-3 py-2 border border-background-200/70 rounded-lg text-sm bg-background-50">
              {auto.dimensionColumns.length === 0 && <option value="">Aucune</option>}
              {auto.dimensionColumns.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-foreground-600 mb-1">Mesure</label>
            <select value={measure} onChange={(e) => setMeasure(e.target.value)} disabled={agg === 'COUNT'} className="w-full px-3 py-2 border border-background-200/70 rounded-lg text-sm bg-background-50 disabled:opacity-50">
              {auto.numericColumns.length === 0 && <option value="">Aucune</option>}
              {auto.numericColumns.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-foreground-600 mb-1">Agrégation</label>
            <select value={agg} onChange={(e) => setAgg(e.target.value as Aggregation)} className="w-full px-3 py-2 border border-background-200/70 rounded-lg text-sm bg-background-50">
              {AGGS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-foreground-600 mb-1">Type</label>
            <select value={chartType} onChange={(e) => setChartType(e.target.value as 'bar' | 'line' | 'pie' | 'column' | 'area')} className="w-full px-3 py-2 border border-background-200/70 rounded-lg text-sm bg-background-50">
              <option value="bar">Barres horizontales</option>
              <option value="column">Colonnes</option>
              <option value="line">Ligne</option>
              <option value="area">Aire</option>
              <option value="pie">Camembert</option>
            </select>
          </div>
        </div>
        <button onClick={generateManual} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer">
          Générer le graphique
        </button>

        {manualPoints && (
          <div className="mt-4">
            <ChartCard
              title={`${agg} de ${agg === 'COUNT' ? dimension : measure} par ${dimension}`}
              chartType={chartType}
              points={manualPoints}
              onSelect={onCrossFilter ? (label) => onCrossFilter(dimension, label) : undefined}
            />
          </div>
        )}
      </div>
    </div>
  );
}